import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { withReturnTo } from '../common/returnPath';
import ShellLink from '../common/ShellLink';
import { crossShellHref } from '../../shell/routeShell';
import type { BuildContextSource } from '../../types';

/**
 * What this build is working from, before you run it.
 *
 * The links already existed and the agent already used them — but the only way
 * to find that out was to run a build and read a chat transcript. The room lived
 * on the subject screen, the trend on a trends card, the rules in a drawer and
 * the research in a tab; four places, none of which said "and this is what
 * reaches the writer".
 *
 * Every row reports READY, not just presence. A linked room with nothing in it
 * and no room at all look the same from outside, and the difference is the
 * whole question of whether to go and fill it before spending a build.
 */

const ICONS: Record<string, string> = {
  trend: '📈',
  boardroom: '🏛️',
  knowledge: '📐',
  research: '🔍',
  documents: '📎',
};

function Row({ source, from }: { source: BuildContextSource; from: string }) {
  const body = (
    <>
      <span aria-hidden="true">{ICONS[source.kind] ?? '•'}</span>
      <span className="font-medium text-gray-800 dark:text-gray-100">{source.label}</span>
      {source.detail && (
        <span className="text-gray-500 dark:text-gray-400">- {source.detail}</span>
      )}
    </>
  );

  return (
    <li
      className={`flex flex-wrap items-center gap-1.5 text-xs ${
        // Dimmed rather than hidden: "there are no rules on this subject" is
        // something worth knowing before a build, not something to tidy away.
        source.ready ? '' : 'opacity-60'
      }`}
    >
      {source.href &&
      crossShellHref(source.href, window.location.hostname, window.location.origin) ? (
        /* The room is another product on another host: a router link would
           render it inside this shell, and a way home cannot cross origins. */
        <ShellLink to={source.href} className="flex flex-wrap items-center gap-1.5 hover:underline">
          {body}
        </ShellLink>
      ) : source.href ? (
        /* Every one of these is a "go and check" link, and checking means doing
           something at the destination — opening a drawer, reading a room. That
           kills browser-back, so each carries an explicit way home. */
        <Link
          to={withReturnTo(source.href, from, 'page builder')}
          className="flex flex-wrap items-center gap-1.5 hover:underline"
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </li>
  );
}

export default function BuildContextStrip({ pageId }: { pageId: number }) {
  const location = useLocation();
  // The page builder path, with whatever the URL is currently carrying.
  //
  // This used to claim it returned you to the PHASE you were on. It never did:
  // the build phase is component state (`active` in PageBuilderWorkflow), not a
  // URL param, so there is nothing in `search` to restore it from and back has
  // always landed at the top of the build. Kept accurate rather than made true —
  // putting the phase in the URL is a bigger change than this strip should make.
  const from = `${location.pathname}${location.search}`;
  const [sources, setSources] = useState<BuildContextSource[] | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let live = true;
    api
      .getBuildContext(pageId)
      .then(res => {
        if (live) setSources(res.sources);
      })
      // Silent: this explains the build, it does not gate it, and an error
      // banner over an explanation would be worse than no explanation.
      .catch(() => {
        if (live) setSources([]);
      });
    return () => {
      live = false;
    };
  }, [pageId]);

  if (!sources || sources.length === 0) return null;

  const readyCount = sources.filter(s => s.ready).length;

  return (
    <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
          Feeding this build · {readyCount} of {sources.length} ready
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {sources.map(s => (
            <Row key={s.kind} source={s} from={from} />
          ))}
        </ul>
      )}
    </div>
  );
}
